import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowLeft, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { getRPEAnalysis } from '../../api';
import { rpeValueColor } from '../WellnessInput';

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };

const TH = {
  padding: '9px 12px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: '1px solid var(--border)',
  background: 'var(--bg-app)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
};
const TD = { padding: '9px 12px', fontSize: 13, color: 'var(--text-primary)', textAlign: 'right', borderBottom: '1px solid var(--border)' };

const todayStr = () => new Date().toISOString().slice(0, 10);
function addDaysStr(dateStr, n) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
const fmtDay = d => new Date(d).toLocaleDateString('nl-NL', { weekday: 'short', day: '2-digit', month: '2-digit' });
const avg = arr => (arr.length ? arr.reduce((s, n) => s + n, 0) / arr.length : null);
const f1 = v => (v == null ? '—' : v.toFixed(1));

// Session load = RPE × duration is the usual sRPE formula, but this page has no per-player
// minutes for gym work, so it deliberately reports raw ratings only — no derived load number
// that would silently be wrong for half the rows.
function Pill({ value }) {
  if (value == null) return <span style={{ color: 'var(--text-dimmed)' }}>—</span>;
  const c = rpeValueColor(value);
  return (
    <span style={{
      display: 'inline-block', minWidth: 26, padding: '2px 7px', borderRadius: 6,
      background: `${c}22`, color: c, fontWeight: 700, fontSize: 12.5,
    }}>{value}</span>
  );
}

function StatTile({ label, value, sub, color }) {
  return (
    <div style={{ ...card, padding: '14px 16px', flex: '1 1 150px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || 'var(--text-primary)', marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function SortHeader({ label, sortKey, activeKey, dir, onClick, align = 'right' }) {
  const active = activeKey === sortKey;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th style={{ ...TH, textAlign: align }} onClick={() => onClick(sortKey)}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: align === 'left' ? 'flex-start' : 'flex-end' }}>
        {label} <Icon size={11} style={{ opacity: active ? 1 : 0.35 }} />
      </span>
    </th>
  );
}

// Stage-4 null-last comparator, same pattern as the other sortable tables in this app.
function compareRows(a, b, key, dir) {
  const av = a[key], bv = b[key];
  const aNull = av == null || av === '';
  const bNull = bv == null || bv === '';
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
  return dir === 'asc' ? cmp : -cmp;
}

export default function RPEAnalysis({ onBack }) {
  const [dateFrom, setDateFrom] = useState(addDaysStr(todayStr(), -13));
  const [dateTo, setDateTo]     = useState(todayStr());
  const [data, setData]         = useState({ entries: [], sessions: [] });
  const [loading, setLoading]   = useState(true);
  const [typeFilter, setTypeFilter] = useState('all'); // all | pitch | gym
  const [sortKey, setSortKey]   = useState('player_name');
  const [sortDir, setSortDir]   = useState('asc');

  const load = useCallback(() => {
    if (!dateFrom || !dateTo || dateFrom > dateTo) return;
    setLoading(true);
    getRPEAnalysis(dateFrom, dateTo)
      .then(res => setData({ entries: res.entries || [], sessions: res.sessions || [] }))
      .catch(() => setData({ entries: [], sessions: [] }))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const entries = useMemo(
    () => data.entries.filter(e => typeFilter === 'all' || (e.session_type || 'pitch') === typeFilter),
    [data.entries, typeFilter]
  );

  const summary = useMemo(() => {
    const pitch = entries.filter(e => (e.session_type || 'pitch') === 'pitch');
    const gym   = entries.filter(e => e.session_type === 'gym');
    return {
      total: entries.length,
      pitchCount: pitch.length,
      gymCount: gym.length,
      avgMuscular: avg(entries.map(e => e.rpe_muscular).filter(v => v != null)),
      // Cardio only exists on pitch rows — averaging across all entries would be meaningless.
      avgCardio: avg(pitch.map(e => e.rpe_cardiovascular).filter(v => v != null)),
      players: new Set(entries.map(e => e.player_id)).size,
    };
  }, [entries]);

  // One block per date, newest first: the day's GPS session titles as context, then a row per
  // player holding whichever of their pitch/gym submissions exist for that date.
  const days = useMemo(() => {
    const byDate = new Map();
    entries.forEach(e => {
      const d = (e.date || '').slice(0, 10);
      if (!byDate.has(d)) byDate.set(d, new Map());
      const players = byDate.get(d);
      if (!players.has(e.player_id)) {
        players.set(e.player_id, { player_id: e.player_id, player_name: e.player_name, position: e.position, pitchM: null, pitchC: null, gymM: null });
      }
      const row = players.get(e.player_id);
      if (e.session_type === 'gym') row.gymM = e.rpe_muscular;
      else { row.pitchM = e.rpe_muscular; row.pitchC = e.rpe_cardiovascular; }
    });
    const sessionsByDate = new Map();
    (data.sessions || []).forEach(s => {
      const d = (s.date || '').slice(0, 10);
      if (!sessionsByDate.has(d)) sessionsByDate.set(d, []);
      const list = sessionsByDate.get(d);
      if (s.session_title && !list.includes(s.session_title)) list.push(s.session_title);
    });
    return [...byDate.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([date, players]) => ({
        date,
        sessions: sessionsByDate.get(date) || [],
        rows: [...players.values()].sort((a, b) => compareRows(a, b, sortKey, sortDir)),
      }));
  }, [entries, data.sessions, sortKey, sortDir]);

  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'player_name' ? 'asc' : 'desc'); }
  };

  const inputStyle = {
    padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-strong)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit',
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <button onClick={onBack}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 18 }}>
        <ArrowLeft size={14} /> Back to Forms
      </button>

      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>RPE Analysis</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4, marginBottom: 20 }}>
        Pitch and gym session ratings per day.
      </p>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>until</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
        <div style={{ display: 'flex', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {[['all', 'All'], ['pitch', '⚽ Pitch'], ['gym', '🏋️ Gym']].map(([k, lbl]) => (
            <button key={k} onClick={() => setTypeFilter(k)}
              style={{
                padding: '7px 13px', border: 'none', cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit',
                background: typeFilter === k ? 'rgba(59,130,246,0.15)' : 'transparent',
                color: typeFilter === k ? '#93c5fd' : 'var(--text-muted)',
                fontWeight: typeFilter === k ? 600 : 400,
              }}>{lbl}</button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <StatTile label="Submissions" value={summary.total} sub={`${summary.pitchCount} pitch · ${summary.gymCount} gym`} />
        <StatTile label="Players" value={summary.players} sub="with at least one entry" />
        <StatTile label="Avg muscular" value={f1(summary.avgMuscular)} sub="all selected entries" color={summary.avgMuscular != null ? rpeValueColor(Math.round(summary.avgMuscular)) : undefined} />
        <StatTile label="Avg cardio" value={f1(summary.avgCardio)} sub="pitch sessions only" color={summary.avgCardio != null ? rpeValueColor(Math.round(summary.avgCardio)) : undefined} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-dimmed)' }}>Loading…</div>
      ) : !days.length ? (
        <div style={{ ...card, padding: 40, textAlign: 'center', color: 'var(--text-dimmed)', fontSize: 14 }}>
          No RPE submissions in this period.
        </div>
      ) : days.map(day => (
        <div key={day.date} style={{ ...card, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '11px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{fmtDay(day.date)}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{day.rows.length} player{day.rows.length !== 1 ? 's' : ''}</span>
            {day.sessions.map(s => (
              <span key={s} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: 'var(--bg-app)', color: 'var(--text-secondary)' }}>{s}</span>
            ))}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead>
                <tr>
                  <SortHeader label="Player"    sortKey="player_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="left" />
                  <SortHeader label="Pitch musc." sortKey="pitchM"    activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Pitch cardio" sortKey="pitchC"   activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Gym musc."  sortKey="gymM"       activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {day.rows.map(r => (
                  <tr key={r.player_id}>
                    <td style={{ ...TD, textAlign: 'left' }}>
                      <span style={{ fontWeight: 600 }}>{r.player_name}</span>
                      {r.position && <span style={{ fontSize: 11, color: 'var(--text-dimmed)', marginLeft: 6 }}>{r.position}</span>}
                    </td>
                    <td style={TD}><Pill value={r.pitchM} /></td>
                    <td style={TD}><Pill value={r.pitchC} /></td>
                    <td style={TD}><Pill value={r.gymM} /></td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--bg-subtle)' }}>
                  <td style={{ ...TD, textAlign: 'left', fontWeight: 700, color: 'var(--text-secondary)' }}>Average</td>
                  <td style={{ ...TD, fontWeight: 700 }}>{f1(avg(day.rows.map(r => r.pitchM).filter(v => v != null)))}</td>
                  <td style={{ ...TD, fontWeight: 700 }}>{f1(avg(day.rows.map(r => r.pitchC).filter(v => v != null)))}</td>
                  <td style={{ ...TD, fontWeight: 700 }}>{f1(avg(day.rows.map(r => r.gymM).filter(v => v != null)))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
